import type { ClientBuilder } from "api-types/builders/clientBuilderTypes";
import type { ClientHomePlanPublic } from "api-types/homePlans/homePlanResponses";
import type {
  ClientLocationPlan,
  ClientLocationPlanBaseData,
  ClientLocationPlanListData,
} from "api-types/locationPlans/locationPlansResponses";
import type { ClientLocationWithLevel } from "api-types/locations/locationBaseTypes";
import OptionSelectCircle from "lib/components/builders/communities/plans/OptionSelectCircle";
import { getCommunityPlanPrice } from "lib/components/builders/communities/utils/getCommunityPlanPrice";
import { FetchErrorMessage } from "lib/components/errors/FetchErrorMessage";
import { getBuilderLocationPlansURL, getBuilderLocationPlanURL } from "lib/endpointHelpers";
import { useFetch } from "lib/fetcher";
import { locationPlanLibraryPath } from "lib/routeHelpers";
import { useMemo } from "react";
import Link from "ui/components/Link";
import Loading from "ui/components/Loading";
import IconExternalLink from "ui/icons/IconExternalLink";
import { ConfigTable } from "ui/tables/DataTable";
import type { Column } from "ui/tables/DataTable/types";
import styles from "./ChooseLocationPlan.module.scss";

export function ChooseLocationPlan({
  location,
  homePlan,
  builder,
  selectedLocationPlan,
  onSelect,
}: {
  builder: ClientBuilder;
  location: ClientLocationWithLevel;
  selectedLocationPlan: ClientLocationPlan | null;
  homePlan: ClientHomePlanPublic;
  onSelect: (locationPlan: ClientLocationPlan) => void;
}) {
  const { data: locationPlansData, error } = useFetch<ClientLocationPlanListData>(
    location
      ? getBuilderLocationPlansURL({
          builderId: builder.id,
          locationId: location.id,
          masterPlanId: homePlan.masterPlanId,
        })
      : "",
    [builder.id, location],
    { handleErrorLocally: true },
  );

  const locationPlanColumns: Column<ClientLocationPlan>[] = useMemo(
    () => [
      {
        header: "Plan Name",
        cell: function SelectedCell(c) {
          return (
            <span className={styles.planName}>
              <OptionSelectCircle selected={c.row.original.id === selectedLocationPlan?.id} />
              {c.row.original.name}
              <Link
                target="_blank"
                href={locationPlanLibraryPath({
                  builderId: builder.id,
                  locationId: location.id,
                  locationPlanId: c.row.original.id,
                })}
              >
                <IconExternalLink size={12} />
              </Link>
            </span>
          );
        },
      },
      {
        header: "Base Price",
        accessor: function BasePriceCell(plan) {
          return <span>{getCommunityPlanPrice(location, plan, "From")}</span>;
        },
        sortType: (rowA, rowB, id, desc) => {
          const [basePriceA] = rowA.values[id].props.children[1].split("K");
          const [basePriceB] = rowB.values[id].props.children[1].split("K");
          const basePriceAValue = parseInt(basePriceA);
          const basePriceBValue = parseInt(basePriceB);

          if (desc) {
            return basePriceAValue < basePriceBValue ? 1 : -1;
          } else {
            return basePriceBValue > basePriceAValue ? 1 : -1;
          }
        },
      },
    ],
    [locationPlansData, selectedLocationPlan?.id],
  );

  if (error) {
    return <FetchErrorMessage fetchError={error} />;
  }

  if (!locationPlansData) {
    return <Loading />;
  }

  return (
    <div>
      Selected location: {location.name}
      <ConfigTable
        data={locationPlansData.locationPlans}
        columns={locationPlanColumns}
        onRowClick={(row) => {
          onSelect(row.original);
        }}
        emptyCTA={<EmptyCta location={location} homePlan={homePlan} builderId={builder.id} />}
        fixedHeader
      />
    </div>
  );
}

function EmptyCta({
  location,
  homePlan,
  builderId,
}: {
  location: ClientLocationWithLevel;
  homePlan: ClientHomePlanPublic;
  builderId: string;
}) {
  const { data: currentLocationPlan } = useFetch<ClientLocationPlanBaseData>(
    getBuilderLocationPlanURL({
      builderId: builderId,
      locationId: location.id,
      locationPlanId: homePlan.communityPlanId,
    }),
    [builderId, location, homePlan],
    { handleErrorLocally: true },
  );

  if (!currentLocationPlan) {
    return null;
  }

  return (
    <>
      {`In order to move this Home Plan into ${location.name}, you must first add a Location Plan that inherits from ${currentLocationPlan.masterPlan.name}`}
    </>
  );
}
